import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3,
  CircleDollarSign,
  Copy,
  Crown,
  Database,
  Gift,
  Info,
  Share,
  Star,
  User,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";
import type {
  LeaderboardEventWithUserStatus,
  LeaderboardUserEntry,
} from "@crypto-trading/common/browser";

/** Extra columns the api adds to each leaderboard row. */
type LeaderboardRow = LeaderboardUserEntry & {
  referrals?: number;
  tradingVolume?: number;
  multiplier?: number;
};

/** How far the user is into a mission, from the api. */
type MissionEvent = LeaderboardEventWithUserStatus & {
  progress?: { current: number; target: number; unit: "count" | "usd" } | null;
};

const money = (value: number) => `$${Math.round(value).toLocaleString("en-US")}`;

/* ---------- Your Referral Code ---------- */

interface ReferralCodeCardProps {
  referralCode: string;
  codeNeedsRefresh: boolean;
  canUseCode: boolean;
  onCopyCode: () => void;
  onShareCode: () => void;
  onGenerateNewCode: () => void;
}

export const ReferralCodeCard: React.FC<ReferralCodeCardProps> = ({
  referralCode,
  codeNeedsRefresh,
  canUseCode,
  onCopyCode,
  onShareCode,
  onGenerateNewCode,
}) => {
  const primary =
    "flex h-10 w-full items-center justify-center gap-2 rounded-xl border-2 border-white/[0.12] bg-[#07a66c] text-sm font-semibold text-white transition-colors hover:bg-[#07a66c]/85";
  return (
    <div className="glass relative flex flex-col gap-4 overflow-hidden !rounded-2xl p-4">
      <img
        src="/referral-code-bg.jpg"
        alt=""
        className="pointer-events-none absolute inset-0 size-full object-cover opacity-50"
      />
      <div className="relative flex items-center gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-full border border-[#2dcc89]/60 bg-[#07a66c]/10">
          <Gift className="size-6 text-[#2dcc89]" />
        </span>
        <div className="flex flex-col">
          <p className="text-base font-semibold leading-6 text-white">Your Referral Code</p>
          <p className="text-xs leading-[18px] text-[#d4d4d4]">
            Share your unique code and start earning points.
          </p>
        </div>
      </div>

      {canUseCode && (
        <div className="relative flex h-11 items-center overflow-hidden rounded-xl border border-white/10 bg-[#0d121a]/80">
          <span
            className={`flex-1 truncate px-3 font-mono text-base ${codeNeedsRefresh ? "text-[#f97066]" : "text-white"}`}
          >
            {codeNeedsRefresh ? "EXPIRED" : referralCode}
          </span>
          {!codeNeedsRefresh && (
            <button
              type="button"
              onClick={() => {
                onCopyCode();
                toast.success("Code copied to clipboard");
              }}
              className="flex h-full items-center gap-1.5 border-l border-white/10 px-4 text-sm font-semibold text-[#d4d4d4] transition-colors hover:bg-white/5"
            >
              <Copy className="size-4" />
              Copy
            </button>
          )}
        </div>
      )}

      <div className="relative flex flex-col gap-2">
        {!canUseCode ? (
          <>
            <Link to="/settings" className={primary}>
              <Zap className="size-5" />
              Connect Exchange
            </Link>
            <p className="flex items-center justify-center gap-1 text-center text-[11px] leading-4 text-[#a3a3a3]">
              <Info className="size-3 shrink-0" />
              Connect your first exchange to get your referral code and start earning.
            </p>
          </>
        ) : codeNeedsRefresh ? (
          <button type="button" onClick={onGenerateNewCode} className={primary}>
            <Zap className="size-5" />
            Get New Code
          </button>
        ) : (
          <button type="button" onClick={onShareCode} className={primary}>
            <Share className="size-5" />
            Share Code
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------- Referrals / Points / Multiplier ---------- */

export const ReferralStatCards: React.FC<{
  referrals: number;
  points: number;
  multiplier: number;
  mobile?: boolean;
}> = ({ referrals, points, multiplier, mobile }) => {
  const items = [
    { label: "Referrals", value: referrals.toLocaleString("en-US"), icon: <Users /> },
    { label: "Points", value: points.toLocaleString("en-US"), icon: <Database /> },
    { label: "Multiplier", value: `${multiplier}x`, icon: <Zap /> },
  ];
  return (
    <div className={`grid grid-cols-3 ${mobile ? "gap-2" : "gap-4"}`}>
      {items.map((item) => (
        <div
          key={item.label}
          className={`glass flex min-w-0 !rounded-xl ${
            mobile ? "flex-col items-center gap-3 px-2 py-3 text-center" : "items-center gap-5 px-6 py-3"
          }`}
        >
          <span className="flex size-12 shrink-0 items-center justify-center rounded-full border border-[#2dcc89]/60 bg-[#07a66c]/10 text-[#2dcc89] [&>svg]:size-6">
            {item.icon}
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="text-sm leading-5 text-[#d4d4d4]">{item.label}</span>
            <span className="truncate text-lg font-semibold leading-7 text-white">{item.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------- Level up your Trading (missions) ---------- */

/** Icon by event key; a star for anything new. */
function missionIcon(eventKey: string) {
  const key = eventKey.toLowerCase();
  if (key.includes("volume") && key.includes("refer")) return <BarChart3 />;
  if (key.includes("volume")) return <CircleDollarSign />;
  if (key.includes("refer") && /\d/.test(key)) return <Users />;
  if (key.includes("refer")) return <User />;
  if (key.includes("position") || key.includes("trade")) return <Star />;
  if (key.includes("join") || key.includes("signup")) return <UserPlus />;
  return <Star />;
}

const reward = (event: LeaderboardEventWithUserStatus) =>
  event.increaseMultiplier ? `${event.increaseMultiplier / 100}x multiplier` : `+${event.points} Points`;

export const MissionsList: React.FC<{
  events: MissionEvent[];
  mobile?: boolean;
}> = ({ events, mobile }) => {
  if (events.length === 0) {
    return <p className="py-6 text-center text-sm text-[#a3a3a3]">No missions this season yet.</p>;
  }
  return (
    <div className="flex flex-col gap-1.5">
      {events.map((event) => {
        const limited = event.eventType === "LIMITED";
        const done = limited ? !!event.completed : (event.completedCount ?? 0) > 0;
        const p = event.progress;
        const fmt = (v: number) => (p?.unit === "usd" ? money(v) : v.toLocaleString("en-US"));
        // Measurable missions show "20 / 25"; the rest done/not done or a count.
        const progressLabel =
          p && !(limited && done)
            ? `${fmt(p.current)} / ${fmt(p.target)}`
            : limited
              ? done
                ? "Completed"
                : "Not completed"
              : `${event.completedCount ?? 0} Times`;
        const fill = p && !(limited && done) ? Math.min(100, (p.current / p.target) * 100) : done ? 100 : 0;
        const bar = (
          <div className="flex w-full flex-col gap-1.5 sm:w-[130px]">
            <span className="text-xs leading-[18px] text-[#d4d4d4]">{progressLabel}</span>
            <span className="h-1.5 w-full overflow-hidden rounded-full bg-white/15">
              <span
                className="block h-full rounded-full bg-[#07a66c]"
                style={{ width: `${Math.max(fill, 4)}%` }}
              />
            </span>
          </div>
        );
        const pill = (
          <span className="shrink-0 whitespace-nowrap rounded-md border border-[#2dcc89] bg-[#07a66c]/10 px-2 py-0.5 text-sm font-medium text-[#2dcc89]">
            {reward(event)}
          </span>
        );
        return (
          <div key={event.id} className="glass flex !rounded-xl p-3">
            <div className={`flex w-full gap-3 ${mobile ? "flex-col" : "items-center"}`}>
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-[#07a66c]/10 text-[#2dcc89] [&>svg]:size-6">
                  {missionIcon(event.eventKey)}
                </span>
                <div className="flex min-w-0 flex-col">
                  <span className="text-sm font-semibold leading-5 text-white">{event.name}</span>
                  <span className="text-xs leading-[18px] text-[#d4d4d4]">{event.description}</span>
                </div>
              </div>
              {mobile ? (
                <div className="flex items-end gap-3">
                  <div className="flex-1">{bar}</div>
                  {pill}
                </div>
              ) : (
                <>
                  {bar}
                  <div className="flex w-[136px] justify-end">{pill}</div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ---------- Leaderboard ---------- */

const displayName = (entry: LeaderboardUserEntry) =>
  entry.username || [entry.firstName, entry.lastName].filter(Boolean).join(" ") || "Trader";

const initials = (entry: LeaderboardUserEntry) =>
  displayName(entry)
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

const CROWN = ["#facc15", "#94a3b8", "#c2410c"];

function multiplierTone(multiplier: number): string {
  if (multiplier >= 3) return "border-[#93370d] bg-[#4e1d09] text-[#fdb022]";
  if (multiplier >= 2) return "border-[#1849a9] bg-[#102a56] text-[#84caff]";
  return "border-white/15 bg-white/5 text-[#d4d4d4]";
}

export const LeaderboardTable: React.FC<{
  leaderboard: LeaderboardRow[];
  /** Phones show only user and points. */
  compact?: boolean;
  /** The viewer's own row, shown under the list when they're not in it. */
  you?: { name: string; rank: number | null; points: number } | null;
  limit?: number;
}> = ({ leaderboard, you, limit = 10, compact = false }) => {
  const rows = leaderboard.slice(0, limit);
  const youListed = rows.some((row) => row.isCurrentUser);
  const detailed = !compact && rows.some((row) => row.referrals !== undefined);
  const Row: React.FC<{
    rank: React.ReactNode;
    crown?: string;
    avatar: string;
    name: string;
    points: number;
    me?: boolean;
    row?: LeaderboardRow;
  }> = ({ rank, crown, avatar, name, points, me, row }) => (
    <tr className={`border-b border-white/[0.06] text-sm last:border-b-0 ${me ? "bg-[#07a66c]/10" : ""}`}>
      <td className="w-12 px-4 py-3.5 text-[#d4d4d4]">{rank}</td>
      <td className="w-8 py-3.5">{crown && <Crown className="size-4" style={{ color: crown }} fill={crown} />}</td>
      <td className="px-3 py-3.5">
        <span className="flex items-center gap-3 font-medium text-white">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#2dcc89]/50 bg-[#262626] text-xs font-semibold text-[#d4d4d4]">
            {avatar}
          </span>
          <span className="truncate">{name}</span>
        </span>
      </td>
      {detailed && <td className="px-3 py-3.5 text-right text-white">{row?.referrals ?? "–"}</td>}
      {detailed && (
        <td className="px-3 py-3.5 text-right text-white">{row?.tradingVolume != null ? money(row.tradingVolume) : "–"}</td>
      )}
      <td className="px-4 py-3.5 text-right font-medium text-white">{points.toLocaleString("en-US")}</td>
      {detailed && (
        <td className="px-4 py-3.5 text-right">
          {row?.multiplier != null && (
            <span className={`rounded-md border px-1.5 py-0.5 text-xs font-medium ${multiplierTone(row.multiplier)}`}>
              {row.multiplier}x
            </span>
          )}
        </td>
      )}
    </tr>
  );

  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-[#a3a3a3]">The leaderboard fills up as the season starts.</p>;
  }
  return (
    <table className="w-full table-fixed">
      <colgroup>
        <col className="w-12" />
        <col className="w-8" />
        <col />
        {detailed && <col className="w-24" />}
        {detailed && <col className="w-32" />}
        <col className="w-28" />
        {detailed && <col className="w-24" />}
      </colgroup>
      <thead>
        <tr className="border-y border-white/10 bg-white/[0.02] text-xs font-semibold text-[#a3a3a3]">
          <th className="px-4 py-3 text-left font-semibold">#</th>
          <th />
          <th className="px-3 py-3 text-left font-semibold">User</th>
          {detailed && <th className="px-3 py-3 text-right font-semibold">Referrals</th>}
          {detailed && <th className="px-3 py-3 text-right font-semibold">Trading Volume</th>}
          <th className="px-4 py-3 text-right font-semibold">Points</th>
          {detailed && <th className="px-4 py-3 text-right font-semibold">Multiplier</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((entry, i) => (
          <Row
            key={entry.userId}
            rank={entry.rank}
            crown={i < 3 ? CROWN[i] : undefined}
            avatar={initials(entry)}
            name={displayName(entry)}
            points={entry.points}
            me={entry.isCurrentUser}
            row={entry}
          />
        ))}
        {you && !youListed && (
          <>
            <tr>
              <td colSpan={detailed ? 7 : 4} className="py-1 text-center text-[#a3a3a3]">…</td>
            </tr>
            <Row
              rank={you.rank ?? "–"}
              avatar={you.name.slice(0, 2).toUpperCase()}
              name={you.name}
              points={you.points}
              me
            />
          </>
        )}
      </tbody>
    </table>
  );
};

/* ---------- How It Works ---------- */

const STEPS: Array<{ title: string; body: React.ReactNode }> = [
  {
    title: "Invite Friends, Earn Points",
    body: "Share your referral link and earn points for each person who joins. The more you bring in, the higher you climb on the leaderboard.",
  },
  {
    title: "Trade to Boost Your Rank",
    body: (
      <>
        Earn points from your own trades and from the trading volume of your referrals. More volume means more
        points, and more <span className="text-[#2dcc89]">$TRIGGER</span> in the end.
      </>
    ),
  },
  {
    title: "Get Airdropped and Win Prizes",
    body: (
      <>
        At the end of the season, your points convert into <span className="text-[#2dcc89]">$TRIGGER</span>{" "}
        airdrops.
      </>
    ),
  },
];

export const HowItWorksCard: React.FC<{ tall?: boolean }> = ({ tall }) => (
  <div className={`glass relative overflow-hidden !rounded-2xl p-4 ${tall ? "min-h-[598px]" : "min-h-[420px]"}`}>
    <img
      src="/how-it-works-bg.jpg"
      alt=""
      className="pointer-events-none absolute inset-0 size-full object-cover object-bottom"
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0d121a] via-[#0d121a]/80 to-transparent" />
    <div className="relative flex flex-col gap-6">
      <p className="text-base font-semibold leading-6 text-white">How It Works?</p>
      <ol className="flex flex-col gap-6">
        {STEPS.map((step, i) => (
          <li key={step.title} className="relative flex gap-3">
            {i < STEPS.length - 1 && (
              <span className="absolute left-[11px] top-7 h-[calc(100%-4px)] border-l border-dashed border-[#2dcc89]/60" />
            )}
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#07a66c] text-xs font-semibold text-white">
              {i + 1}
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold leading-5 text-white">{step.title}</span>
              <span className="text-sm leading-5 text-[#d4d4d4]">{step.body}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  </div>
);
